import { useMemo, useState } from 'react'
import { Globe, Pencil, Plus, Search, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { InventoryEditDialog } from '@/components/doer/inventory-edit-dialog'
import { LinkToGlobalDialog } from '@/components/catalog/link-to-global-dialog'
import { LinkedToGlobalChip } from '@/components/catalog/linked-to-global-chip'
import {
  useInventory,
  useDeleteInventoryItem,
  useLinkInventoryToGlobal,
} from '@/features/doer/use-inventory'
import type { ApiError, GlobalCatalogKind, InventoryItem } from '@/api/types'

const formatQty = (qty: number): string => {
  if (Number.isInteger(qty)) return String(qty)
  return qty.toFixed(2).replace(/\.?0+$/, '')
}

const apiErrorMessage = (err: unknown, fallback: string): string =>
  (err as ApiError | undefined)?.detail ?? fallback

const InventoryRow = ({
  item,
  onEdit,
  onDelete,
  onLink,
}: {
  item: InventoryItem
  onEdit: () => void
  onDelete: () => void
  onLink: () => void
}) => {
  const isBound = (item.globalCatalogId ?? null) != null
  return (
    <Card>
      <CardContent className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <p className="truncate text-base font-medium">{item.name}</p>
            {isBound && <LinkedToGlobalChip />}
          </div>
          <p className="text-xs text-muted-foreground">
            {formatQty(item.quantity)} {item.unit}
            {item.notes && <> · {item.notes}</>}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {!isBound && (
            <Button variant="outline" size="sm" onClick={onLink} className="gap-1.5">
              <Globe className="h-3.5 w-3.5" />
              Link
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onEdit} className="gap-1.5">
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            className="gap-1.5 text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

const SkeletonList = () => (
  <div className="space-y-2">
    {Array.from({ length: 4 }).map((_, i) => (
      <Skeleton key={i} className="h-16" />
    ))}
  </div>
)

const EmptyState = ({ onAdd }: { onAdd: () => void }) => (
  <Card>
    <CardContent className="flex flex-col items-center justify-center gap-4 py-16 text-center">
      <div>
        <h2 className="text-lg font-semibold">Your inventory is empty.</h2>
        <p className="max-w-sm text-sm text-muted-foreground">
          Add materials and tools you already own. They get subtracted from the BOMs of every
          Project you start, so you can see exactly what you still need to buy.
        </p>
      </div>
      <Button onClick={onAdd} className="gap-2">
        <Plus className="h-4 w-4" />
        Add item
      </Button>
    </CardContent>
  </Card>
)

export const InventoryPage = () => {
  const { data, isLoading, isError, refetch, isFetching } = useInventory()
  const remove = useDeleteInventoryItem()
  const link = useLinkInventoryToGlobal()

  const [filter, setFilter] = useState('')
  const [editing, setEditing] = useState<InventoryItem | null>(null)
  const [creating, setCreating] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<InventoryItem | null>(null)
  const [linking, setLinking] = useState<InventoryItem | null>(null)

  const handleBindInventory = (globalId: string, kind: GlobalCatalogKind) => {
    if (!linking) return
    link.mutate(
      { id: linking.id, request: { globalCatalogId: globalId, globalCatalogKind: kind } },
      {
        onSuccess: () => {
          toast.success(`Linked "${linking.name}" to shared catalog`)
          setLinking(null)
        },
        onError: (err) => toast.error(apiErrorMessage(err, 'Could not link to shared catalog')),
      }
    )
  }

  const items = useMemo(() => data ?? [], [data])
  const filtered = useMemo(() => {
    if (!filter.trim()) return items
    const q = filter.trim().toLowerCase()
    return items.filter((i) => i.name.toLowerCase().includes(q) || i.unit.toLowerCase().includes(q))
  }, [items, filter])

  const handleConfirmDelete = (): void => {
    if (!pendingDelete) return
    remove.mutate(pendingDelete.id, {
      onSuccess: () => toast.success('Removed from inventory'),
      onError: (err) => toast.error(apiErrorMessage(err, 'Could not delete')),
    })
    setPendingDelete(null)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Inventory</h1>
          <p className="text-muted-foreground">
            Materials and tools you already own — applied to every Project you&apos;re building.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Add item
        </Button>
      </div>

      {isError ? (
        <Alert variant="destructive">
          <AlertTitle>Failed to load inventory</AlertTitle>
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>We couldn&apos;t reach the server. Try again?</span>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : isLoading ? (
        <SkeletonList />
      ) : items.length === 0 ? (
        <EmptyState onAdd={() => setCreating(true)} />
      ) : (
        <>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by name or unit"
              className="pl-9"
            />
          </div>
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No items match that filter.
            </p>
          ) : (
            <div className="space-y-2">
              {filtered.map((item) => (
                <InventoryRow
                  key={item.id}
                  item={item}
                  onEdit={() => setEditing(item)}
                  onDelete={() => setPendingDelete(item)}
                  onLink={() => setLinking(item)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <InventoryEditDialog open={creating} onOpenChange={setCreating} />
      <InventoryEditDialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        item={editing}
      />
      {linking && (
        <LinkToGlobalDialog
          open={linking !== null}
          onOpenChange={(open) => !open && setLinking(null)}
          kind="MATERIAL"
          allowKindToggle
          seedName={linking.name}
          seedUnit={linking.unit}
          pending={link.isPending}
          onPickExisting={handleBindInventory}
          onPickNewlyCreated={handleBindInventory}
        />
      )}

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete from inventory?</AlertDialogTitle>
            <AlertDialogDescription>
              Personalized BOMs across your started Projects will stop subtracting this item.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
